import React, { useState } from 'react';
import { useAccessibility } from './AccessibilityProvider';
import './accessibility.css';

const AccessibilityControls = ({ className = '' }) => {
    const {
        screenReaderMode,
        highContrast,
        reducedMotion,
        fontSize,
        setScreenReaderMode,
        setHighContrast,
        setReducedMotion,
        setFontSize,
        announce
    } = useAccessibility();

    const [isExpanded, setIsExpanded] = useState(false);

    const fontSizeOptions = [
        { value: 'small', label: 'Small', description: 'Smaller text size' },
        { value: 'medium', label: 'Medium', description: 'Default text size' },
        { value: 'large', label: 'Large', description: 'Larger text size' },
        { value: 'extra-large', label: 'Extra Large', description: 'Largest text size' }
    ];

    const handleToggleExpanded = () => {
        setIsExpanded(!isExpanded);
        announce(isExpanded ? 'Accessibility controls collapsed' : 'Accessibility controls expanded');
    };

    const handleScreenReaderModeChange = (enabled) => {
        setScreenReaderMode(enabled);
        announce(`Screen reader mode ${enabled ? 'enabled' : 'disabled'}`);
    };

    const handleHighContrastChange = (enabled) => {
        setHighContrast(enabled);
        announce(`High contrast mode ${enabled ? 'enabled' : 'disabled'}`);
    };

    const handleReducedMotionChange = (enabled) => {
        setReducedMotion(enabled);
        announce(`Reduced motion ${enabled ? 'enabled' : 'disabled'}`);
    };

    const handleFontSizeChange = (size) => {
        setFontSize(size);
        const option = fontSizeOptions.find(opt => opt.value === size);
        announce(`Font size changed to ${option?.label || size}`);
    };

    return (
        <div className={`accessibility-controls ${className}`}>
            <button
                className="accessibility-controls__toggle"
                onClick={handleToggleExpanded}
                aria-expanded={isExpanded}
                aria-controls="accessibility-controls-panel"
                aria-label="Toggle accessibility controls"
                title="Accessibility Settings"
            >
                <span className="accessibility-controls__icon" aria-hidden="true">
                    ♿
                </span>
                <span className="accessibility-controls__text">
                    Accessibility
                </span>
            </button>

            {isExpanded && (
                <div
                    id="accessibility-controls-panel"
                    className="accessibility-controls__panel"
                    role="region"
                    aria-label="Accessibility Settings"
                >
                    <h3 className="accessibility-controls__title">
                        Accessibility Settings
                    </h3>

                    {/* Screen Reader Mode */}
                    <div className="accessibility-controls__group">
                        <label className="accessibility-controls__label">
                            <input
                                type="checkbox"
                                checked={screenReaderMode}
                                onChange={(e) => handleScreenReaderModeChange(e.target.checked)}
                                className="accessibility-controls__checkbox"
                                aria-describedby="screen-reader-help"
                            />
                            <span className="accessibility-controls__label-text">
                                Screen Reader Mode
                            </span>
                        </label>
                        <div id="screen-reader-help" className="accessibility-controls__help">
                            Optimizes the interface for screen readers
                        </div>
                    </div>

                    {/* High Contrast */}
                    <div className="accessibility-controls__group">
                        <label className="accessibility-controls__label">
                            <input
                                type="checkbox"
                                checked={highContrast}
                                onChange={(e) => handleHighContrastChange(e.target.checked)}
                                className="accessibility-controls__checkbox"
                                aria-describedby="high-contrast-help"
                            />
                            <span className="accessibility-controls__label-text">
                                High Contrast
                            </span>
                        </label>
                        <div id="high-contrast-help" className="accessibility-controls__help">
                            Increases contrast for better visibility
                        </div>
                    </div>

                    {/* Reduced Motion */}
                    <div className="accessibility-controls__group">
                        <label className="accessibility-controls__label">
                            <input
                                type="checkbox"
                                checked={reducedMotion}
                                onChange={(e) => handleReducedMotionChange(e.target.checked)}
                                className="accessibility-controls__checkbox"
                                aria-describedby="reduced-motion-help"
                            />
                            <span className="accessibility-controls__label-text">
                                Reduce Motion
                            </span>
                        </label>
                        <div id="reduced-motion-help" className="accessibility-controls__help">
                            Minimizes animations and transitions
                        </div>
                    </div>

                    {/* Font Size */}
                    <div className="accessibility-controls__group">
                        <fieldset className="accessibility-controls__fieldset">
                            <legend className="accessibility-controls__legend">
                                Font Size
                            </legend>
                            <div className="accessibility-controls__radio-group">
                                {fontSizeOptions.map((option) => (
                                    <label
                                        key={option.value}
                                        className="accessibility-controls__radio-label"
                                    >
                                        <input
                                            type="radio"
                                            name="fontSize"
                                            value={option.value}
                                            checked={fontSize === option.value}
                                            onChange={(e) => handleFontSizeChange(e.target.value)}
                                            className="accessibility-controls__radio"
                                            aria-describedby={`font-size-${option.value}-help`}
                                        />
                                        <span className="accessibility-controls__radio-text">
                                            {option.label}
                                        </span>
                                        <div
                                            id={`font-size-${option.value}-help`}
                                            className="accessibility-controls__help accessibility-controls__help--inline"
                                        >
                                            {option.description}
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </fieldset>
                    </div>

                    {/* Reset Button */}
                    <div className="accessibility-controls__actions">
                        <button
                            className="accessibility-controls__reset"
                            onClick={() => {
                                setScreenReaderMode(false);
                                setHighContrast(false);
                                setReducedMotion(false);
                                setFontSize('medium');
                                announce('Accessibility settings reset to defaults');
                            }}
                            aria-label="Reset all accessibility settings to defaults"
                        >
                            Reset to Defaults
                        </button>
                    </div>

                    {/* Close Button */}
                    <button
                        className="accessibility-controls__close"
                        onClick={handleToggleExpanded}
                        aria-label="Close accessibility controls"
                    >
                        <span aria-hidden="true">×</span>
                    </button>
                </div>
            )}
        </div>
    );
};

export default AccessibilityControls; 